import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';

import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ColorVariant } from '@psei/ng/api';
import { DialogService, LegacyDialogConfig } from '@psei/ng/dialog';
import { isEqualWith } from 'lodash';

import { FormChangeService } from 'projects/ppb-shared-library/src/lib/services/form-change.service';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AccountDetailsModel, AccountIalEnum } from '../../shared/models/account.model';
import { UserElementalRole } from '../../shared/models/user-elemental-role';
import { UserService } from '../../shared/services/user.service';
import { AccountService } from '../account.service';
import { PpbAccountDataFormBuilder } from './ppb-account-data-form.builder';

@Component({
  selector: 'ppb-account-data',
  templateUrl: './ppb-account-data.component.html',
  styleUrls: ['./ppb-account-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PpbAccountDataComponent implements OnInit, OnDestroy {
  public accountForm: UntypedFormGroup;
  public smsConfirmForm: UntypedFormGroup;
  public rulesConfirmForm: UntypedFormGroup;
  public isDataChanged$ = new BehaviorSubject(false);
  public showChangeDataInfo$ = new BehaviorSubject(false);
  public showEmailChanged$ = new BehaviorSubject(false);
  public showPhoneChanged$ = new BehaviorSubject(false);
  public isSharePhoneChanged$ = new BehaviorSubject(false);
  public sharePhoneChangeValue$ = new BehaviorSubject(false);
  public isConfirmButtonLoading$ = new BehaviorSubject(false);
  public isButtonDisabled$: Observable<boolean>;
  public isPhoneChanged = false;
  public readonly = false;
  public updateId: string;
  public isPopupBlocked = false;
  public account: AccountDetailsModel;
  @ViewChild('smsCodeTemplate') smsCodeTemplate: TemplateRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: PpbAccountDataFormBuilder,
    private accountService: AccountService,
    private dialogBoxService: DialogService,
    private cd: ChangeDetectorRef,
    private formChangeService: FormChangeService,
    private userService: UserService
  ) {
  }

  ngOnDestroy(): void {
    this.formChangeService.clearIsDirty()
  }

  ngOnInit(): void {
    this.account = this.route.snapshot.data.account;
    this.accountForm = this.formBuilder.getAccountUpdateForm();
    this.smsConfirmForm = this.formBuilder.getSMSConfirmForm();
    this.rulesConfirmForm = this.formBuilder.getRulesConfirmForm();
    this.formChangeService.setupDirtyWatch(this.isDataChanged$);

    this.accountForm.patchValue(this.account);
    if (!this.userService.hasRole(UserElementalRole.ACC_UPDATE)) {
      this.accountForm.disable();
      return;
    }
    this.setupFormListeners();
    this.setupButtonState();
  }

  confirmDataChange(): void {
    this.disableForm();
    this.rulesConfirmForm.controls.rulesConfirmed.disable();
    this.cd.markForCheck();
    this.isConfirmButtonLoading$.next(true);
    this.accountService.updateAccount(this.account.id, this.accountForm.getRawValue())
      .subscribe(({ updateId }) => {
        this.smsConfirmForm = this.formBuilder.getSMSConfirmForm(this.isPhoneChanged);
        this.updateId = updateId;
        this.showSMSConfirmDialog();
        this.cd.markForCheck();
      }, (_err) => {
        this.enableForm();
        this.rulesConfirmForm.controls.rulesConfirmed.patchValue(false);
        this.rulesConfirmForm.controls.rulesConfirmed.enable();
        this.isConfirmButtonLoading$.next(false);
      });
  }

  getNewPhoneSmsCodeLabel(): string {
    return $localize`:@@account.phone_sms_code:Podaj kod sms wysłany na numer telefonu
      ${this.accountForm.get('phone.number').value}` + ':'
  }

  getCurrentPhoneSmsCodeLabel(isPhoneChanged: boolean): string {
    return isPhoneChanged
      ? $localize`:@@account.phone_sms_code:Podaj kod sms wysłany na numer telefonu ${this.account.phone.number}` + ':'
      : $localize`:@@common.enter_sms_code_colon:Podaj kod sms: `
  }

  toggleAcceptInfo(): void {
    this.rulesConfirmForm.controls.rulesConfirmed.patchValue(!this.rulesConfirmForm.controls.rulesConfirmed.value);
  }

  enableForm(): void {
    this.readonly = false;
    this.accountForm.controls.sharePhone.enable();
    this.accountForm.controls.expectedIal.enable();
    this.accountForm.controls.phone.enable();
  }

  disableForm(): void {
    this.readonly = true;
    this.accountForm.controls.sharePhone.disable();
    this.accountForm.controls.expectedIal.disable();
    this.accountForm.controls.phone.disable();
  }

  abortDataChange(): void {
    this.formChangeService.executeDialog(() => {
      this.accountForm.patchValue(this.account);
      this.enableForm();
      this.rulesConfirmForm.controls.rulesConfirmed.patchValue(false);
      this.rulesConfirmForm.controls.rulesConfirmed.enable();
      this.cd.markForCheck();
    })
  }

  ialAboveLow(): boolean {
    return this.account.ial !== AccountIalEnum.LOW;
  }

  private showSMSConfirmDialog(): void {
    this.dialogBoxService.legacy.open(this.generateSMSDialog());
  }

  private handleDialogCancel = () => {
    if (this.isPopupBlocked) {
      return;
    }
    this.smsConfirmForm.reset();
    this.enableForm();
    this.toggleAcceptInfo();
    this.isConfirmButtonLoading$.next(false);
    this.rulesConfirmForm.controls.rulesConfirmed.enable();
    this.cd.markForCheck();
    this.dialogBoxService.legacy.closeCurrent();
  };

  private handleDialogConfirm = () => {
    if (this.isPopupBlocked) {
      return;
    }

    const { currentPhoneSmsCode, newPhoneSmsCode } = this.smsConfirmForm.controls;

    if (!currentPhoneSmsCode.value || (this.isPhoneChanged && !newPhoneSmsCode.value)) {
      this.smsConfirmForm.updateValueAndValidity();
      return;
    }

    this.isPopupBlocked = true;
    this.accountService.confirmUpdateAccount(
      this.account.id, this.updateId, currentPhoneSmsCode.value,
      this.isPhoneChanged ? newPhoneSmsCode.value : null
    )
      .subscribe((account: AccountDetailsModel) => {
          this.account = account;
          this.accountForm.patchValue(this.account);
          this.enableForm();
          this.rulesConfirmForm.controls.rulesConfirmed.patchValue(false);
          this.rulesConfirmForm.controls.rulesConfirmed.enable();
          this.isConfirmButtonLoading$.next(false);
          this.smsConfirmForm.reset();
          this.dialogBoxService.legacy.closeCurrent();
          this.isPopupBlocked = false;
          this.cd.markForCheck();

        },
        (_err) => {
          this.isPopupBlocked = false;
          this.handleDialogCancel();
          this.dialogBoxService.legacy.closeCurrent();
        });
  };

  private generateSMSDialog(): LegacyDialogConfig<string> {
    return {
      title: $localize`:@@account.confirmation_data_changed:Potwierdzenie zmiany danych`,
      content: this.smsCodeTemplate,
      buttons: [
        {
          label: $localize`:@@common.cancel:Anuluj`,
          colorVariant: ColorVariant.Secondary,
          onSelect: this.handleDialogCancel
        },
        {
          label: $localize`:@@common.confirm:Potwierdź`,
          colorVariant: ColorVariant.Primary,
          onSelect: this.handleDialogConfirm
        }
      ]
    };
  }

  private handleFormControlSubscription = (isChanged: boolean) => {
    if (!this.showChangeDataInfo$.getValue()) {
      this.showChangeDataInfo$.next(isChanged);
    }
  };

  private setupButtonState = () => {
    this.isButtonDisabled$ = combineLatest([
      this.isDataChanged$,
      this.isConfirmButtonLoading$,
      this.rulesConfirmForm.controls.rulesConfirmed.valueChanges.pipe((startWith(false)))
    ]).pipe(map(([isDataChanged, isButtonLoading, isRulesConfirmed]) => {
      const isPhoneEmailOrShareChanged = this.showEmailChanged$.value || this.showPhoneChanged$.value || this.isSharePhoneChanged$.value;
      if (isPhoneEmailOrShareChanged) {
        return !isRulesConfirmed || isButtonLoading;
      } else {
        if (isDataChanged && (this.ialAboveLow())) {
          return !isRulesConfirmed || isButtonLoading
        } else {
          return isButtonLoading;
        }
      }
    }));
  }

  private setupFormListeners = () => {
    this.accountForm.valueChanges.subscribe((newValues) => {
      let isValueChanged = false;

      Object.keys(this.accountForm.controls).forEach((key: string) => {
        // @ts-ignore
        if (newValues[key]?.trim?.() !== this.account[key]?.trim?.()) {
          isValueChanged = true;
        }
      });

      if (this.account.sharePhone !== this.accountForm.controls.sharePhone.value) {
        isValueChanged = true;
      }

      if (!isEqualWith(this.accountForm.get('phone').value, this.account.phone)) {
        isValueChanged = true;
      }

      this.isDataChanged$.next(!this.accountForm.invalid && isValueChanged);
    });
    this.accountForm.controls.firstName.valueChanges.subscribe((newFirstName) => {
      this.handleFormControlSubscription(newFirstName.trim() !== this.account.firstName);
    });
    this.accountForm.controls.lastName.valueChanges.subscribe((newLastName) => {
      this.handleFormControlSubscription(newLastName.trim() !== this.account.lastName);

    });
    this.accountForm.controls.expectedIal.valueChanges.subscribe((newExpectedIal) => {
      this.handleFormControlSubscription(newExpectedIal !== this.account.expectedIal);
    });
    this.accountForm.controls.email.valueChanges.subscribe((newEmail) => {
      const { email } = this.accountForm.controls;
      this.showEmailChanged$.next(!email.invalid && newEmail !== this.account.email);
    });
    this.accountForm.controls.phone.valueChanges.subscribe((newPhone) => {
      const { phone } = this.accountForm.controls;
      this.isPhoneChanged = !isEqualWith(newPhone, this.account.phone);
      this.showPhoneChanged$.next(!phone.invalid && this.isPhoneChanged);
    });

    this.accountForm.controls.sharePhone.valueChanges.subscribe((newSharePhone) => {
      this.isSharePhoneChanged$.next(newSharePhone !== this.account.sharePhone);
      this.sharePhoneChangeValue$.next(newSharePhone);
    });

    this.isDataChanged$.subscribe((isDataChanged) => {
      if (!isDataChanged) {
        this.rulesConfirmForm.controls.rulesConfirmed.patchValue(false);
        this.showChangeDataInfo$.next(false);
      }
    })
  }

  submitOnEnter($event: KeyboardEvent): void {
    if ($event.key === 'Enter') {
      this.handleDialogConfirm();
    }
  }
}
